import React from "react";
import { P } from "../../components/Type";
import {
  Box,
  Text,
  VStack,
  Flex,
  Grid,
  Show,
  Spinner,
  Skeleton,
} from "@chakra-ui/react";
import Button from "../../components/Button";
import { Trans, msg, t } from "@lingui/macro";
import { useRequest } from "../../contexts/request";
import { useLingui } from "@lingui/react";
import WishlistCardPublic from "./WishlistCardPublic";
import WishlistCardHorizontalPublic from "./WishlistCardHorizontalPublic";
import copyHappy from "../../assets/capy_happy.svg";
import Image from "next/image";
import { useRouter } from "next/router";

const ListWishlistPublic = ({
  plus,
  overlay,
  vote,
  streamerId,
  isGopayEnabled,
  isOvoEnabled,
  isDanaEnabled,
  isLinkAjaEnabled,
  isQRISEnabled,
  isBCAVAEnabled,
  isMandiriVAEnabled,
  isBRIVAEnabled,
  isBNIVAEnabled,
  isPermataVAEnabled,
  isBJBVAEnabled,
  isBSIVAEnabled,
  isCIMBVAEnabled,
  currUsername = "",
  streamerUsername = "",
  currEmail = "",
  currency,
  transactionFeePolicy,
  onSelectWishlist,
}) => {
  const { _ } = useLingui();
  const { get } = useRequest();
  const router = useRouter();

  const [activeTab, setActiveTab] = React.useState("ACTIVE");
  const [page, setPage] = React.useState(1);
  const [wishlists, setWishlists] = React.useState([]);
  const [canFetchMore, setCanFetchMore] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const loaderRef = React.useRef(null);
  const pageLimit = 12;

  // Fetch wishlist page
  const fetchWishlists = async (pageNumber = 1) => {
    setIsLoading(true);
    try {
      const {
        json: { data },
      } = await get(
        `wishlists/public/${streamerUsername}?page=${pageNumber}&limit=${pageLimit}&status=${activeTab}`
      );

      if (pageNumber === 1) {
        setWishlists(data.wishlists || []);
      } else {
        setWishlists((prev) => [...prev, ...(data.wishlists || [])]);
      }

      setCanFetchMore(data.wishlists?.length === pageLimit);
    } catch (err) {
      console.error("Failed to fetch wishlists", err);
    } finally {
      setIsLoading(false);
    }
  };

  // load initial
  React.useEffect(() => {
    setPage(1);
    setWishlists([]);
    fetchWishlists(1);
  }, [activeTab]);

  // observer untuk infinite scroll
  React.useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && canFetchMore && !isLoading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchWishlists(nextPage);
        }
      },
      { threshold: 1.0 }
    );
    observer.observe(loaderRef.current);

    return () => {
      observer.disconnect();
    };
  }, [canFetchMore, isLoading, page]);

  return (
    <Box>
      <VStack
        spacing="12px"
        align="flex-start"
        w="100%"
        mt={{ base: "0px", md: "8px" }}
      >
        <Show above="md">
          <P>
            <Text>
              <Trans>
                Dukung wishlist-nya & intip barang yang udah dibeli streamer
                yuk!
              </Trans>
            </Text>
          </P>
        </Show>
        <Flex direction="row" justifyContent="left" w="100%" overflowX="auto">
          <Button
            label={t`Barang Impian`}
            pressed={activeTab === "ACTIVE"}
            backgroundColor={
              activeTab === "ACTIVE"
                ? "saweria-yellow.normal"
                : "saweria-white.normal"
            }
            px={{ base: 2, md: 6 }}
            flexShrink={0}
            mr={{ base: 2, md: 4 }}
            mb={{ base: 2, md: 6 }}
            boxShadow="none"
            fontSize={{ base: "xs", md: "lg" }}
            onClick={() => setActiveTab("ACTIVE")}
          />
          <Button
            label={t`Barang Udah Dibeli`}
            pressed={activeTab === "END"}
            backgroundColor={
              activeTab === "END"
                ? "saweria-yellow.normal"
                : "saweria-white.normal"
            }
            px={{ base: 2, md: 6 }}
            flexShrink={0}
            mb={{ base: 2, md: 6 }}
            boxShadow="none"
            fontSize={{ base: "xs", md: "lg" }}
            onClick={() => setActiveTab("END")}
          />
        </Flex>
        <Show below="md">
          <P>
            <Text mb={4} mt={-2}>
              <Trans>
                Dukung wishlist-nya & intip barang yang udah dibeli streamer
                yuk!
              </Trans>
            </Text>
          </P>
        </Show>
      </VStack>

      <Grid
        templateColumns={
          isLoading || wishlists.length === 0
            ? { base: "1fr", md: "1fr" }
            : activeTab === "END"
            ? { base: "1fr", md: "1fr" }
            : { base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }
        }
        gap={6}
        justifyContent="start"
      >
        {isLoading ? (
          // ✅ Loading state
          <>
            <Skeleton height="200px" borderRadius="md" />
            <Skeleton height="200px" borderRadius="md" />
            <Skeleton height="200px" borderRadius="md" />
          </>
        ) : wishlists.length === 0 ? (
          // ✅ Empty state (hanya muncul kalau bukan loading dan data kosong)
          <Flex
            flexDir="column"
            align="center"
            justify="center"
            w="full"
            mx="auto"
            py={20}
            gap={{base:0, md: 4}}
            color="gray.700"
            fontFamily="'IBM Plex Mono'"
          >
            <Box w="153px">
              <Image src={copyHappy} alt="empty" boxSize="153px" mb={4} />
            </Box>

            <Text fontSize="lg" fontWeight="500" textAlign="center">
              {activeTab === "END"
                ? t`Belum Ada Pembelian, Sabar ya!`
                : t`Wishlist-nya kosong, tungguin ya!`}
            </Text>

            <Text fontSize="sm" textAlign="center" color="gray.500">
              {activeTab === "END"
                ? t`Wishlist yang dibeli streamer bakal muncul di sini`
                : t`Nanti muncul di sini pas creator bikin wishlist`}
            </Text>

            <Button
              mt={6}
              bg="#96D2D9"
              border="1px solid black"
              _hover={{ bg: "#7fbfc7" }}
              onClick={() =>
                activeTab === "END"
                  ? setActiveTab("ACTIVE")
                  : setActiveTab("END")
              }
              label={
                activeTab === "END"
                  ? _(msg`Lihat Barang Impian`)
                  : _(msg`Lihat Barang Udah Dibeli`)
              }
            />
          </Flex>
        ) : (
          // ✅ Data ada
          wishlists.map((props) =>
            activeTab === "END" ? (
              <WishlistCardHorizontalPublic
                key={`wish-${props.product_slug}`}
                username={streamerUsername}
                currency={currency}
                wishlist={props}
                onClick={() =>
                  router.push(
                    `/${streamerUsername}/wishlist/${props.product_slug}`
                  )
                }
                cursor="pointer"
                
              />
            ) : (
              <WishlistCardPublic
                key={`wish-${props.product_slug}`}
                username={streamerUsername}
                currency={currency}
                wishlist={props}
                onClick={() =>
                  router.push(
                    `/${streamerUsername}/wishlist/${props.product_slug}`
                  )
                }
                cursor="pointer"
                
              />
            )
          )
        )}
      </Grid>

      {/* sentinel untuk observer */}
      <Box
        ref={loaderRef}
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {isLoading && <Spinner size="lg" />}
        {!canFetchMore && !isLoading && wishlists.length > 0 && <></>}
      </Box>
    </Box>
  );
};

export { ListWishlistPublic };
